import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getShopById, getHomeServices } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import Toast from "../components/Toast";

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api" });

export default function ShopDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [shop, setShop] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [homeServices, setHomeServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("services");
  const [copiedCode, setCopiedCode] = useState("");
  const [toast, setToast] = useState({ message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ message, type });

  useEffect(() => {
    const fetchShop = async () => {
      try {
        const res = await getShopById(id);
        setShop(res.data);
        try {
          const couponRes = await axios.get(`${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/coupons/shop/${id}/public`);
          setCoupons(couponRes.data || []);
        } catch { setCoupons([]); }
        try {
          const homeRes = await getHomeServices(id);
          const data = homeRes.data;
          setHomeServices(Array.isArray(data.services) ? data.services : Array.isArray(data) ? data : []);
        } catch { setHomeServices([]); }
      } catch { setError("Shop not found."); }
      finally { setLoading(false); }
    };
    fetchShop();
  }, [id]);

  const handleBookNow = () => {
    if (!user) navigate("/login");
    else if (user.role !== "customer") showToast("Only customers can book appointments.", "warning");
    else navigate(`/book/${id}`);
  };

  const handleHomeBooking = () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "customer") { showToast("Only customers can request home visits.", "warning"); return; }
    navigate(`/home-booking/${id}`, { state: { availableServices: homeServices } });
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(""), 2000);
    });
  };

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.loadingSpinner}>✂️</div>
        <p style={{ color: "var(--text-muted)", marginTop: "12px" }}>Loading shop details...</p>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div style={styles.center}>
        <p style={{ fontSize: "48px" }}>😕</p>
        <p style={{ color: "var(--accent)", fontSize: "16px", marginBottom: "16px" }}>{error}</p>
        <button onClick={() => navigate("/shops")} style={styles.backBtnSmall}>← Go Back</button>
      </div>
    );
  }

  const minPrice = shop.services?.length > 0 ? Math.min(...shop.services.map((s) => s.price)) : null;
  const homeEnabled = shop.home_service_enabled && homeServices.length > 0;

  return (
    <div style={styles.page}>

      {/* Banner */}
      <div style={styles.bannerWrap}>
        {shop.photo ? (
          <img src={shop.photo} alt={shop.name} style={styles.bannerImg} />
        ) : (
          <div style={styles.bannerPlaceholder}>
            <span style={{ fontSize: "64px" }}>✂️</span>
            <span style={styles.bannerPlaceholderLetter}>{shop.name?.charAt(0).toUpperCase()}</span>
          </div>
        )}
        <button
          onClick={() => user?.role === "barber" ? navigate("/dashboard") : navigate("/shops")}
          style={styles.backBtnOverlay}
        >
          {user?.role === "barber" ? "← Dashboard" : "← Back"}
        </button>
        <div style={styles.bannerOverlay} />
        <div style={styles.bannerNameWrap}>
          <h1 style={styles.bannerName}>{shop.name}</h1>
          <p style={styles.bannerCity}>📍 {shop.address}, {shop.city}</p>
          {homeEnabled && (
            <span style={styles.homeBadgeBanner}>🏠 Home service available</span>
          )}
        </div>
      </div>

      {/* Shop Info Card */}
      <div style={styles.infoCard}>
        <div style={styles.metaRow}>
          <span style={styles.ratingBadge}>
            ⭐ {shop.avg_rating > 0 ? Number(shop.avg_rating).toFixed(1) : "New"}
            {shop.total_reviews > 0 && (
              <span style={{ color: "var(--text-muted)", fontWeight: "normal" }}> ({shop.total_reviews} reviews)</span>
            )}
          </span>
          <span style={styles.hoursBadge}>
            🕐 {formatTime(shop.opening_time)} – {formatTime(shop.closing_time)}
          </span>
          {homeEnabled && (
            <span style={styles.homeServiceBadge}>🏠 Home visits</span>
          )}
        </div>
        {shop.description && <p style={styles.shopDesc}>{shop.description}</p>}
        {shop.phone && <p style={styles.phone}>📞 {shop.phone}</p>}
        {!shop.is_subscribed && (
          <div style={styles.notSubscribed}>
            ⚠️ This shop is currently not accepting online bookings
          </div>
        )}
        {shop.is_subscribed && (
          <div style={styles.btnGroup}>
            <button onClick={handleBookNow} style={styles.bookBtn}>
              📅 Book Appointment
            </button>
            {homeEnabled && (
              <button onClick={handleHomeBooking} style={styles.bookHomeBtnCard}>
                🏠 Book Home Visit
              </button>
            )}
          </div>
        )}
      </div>

      {/* Coupons */}
      {coupons.length > 0 && activeTab !== "home" && (
        <div style={styles.couponsSection}>
          <div style={styles.couponsHeader}>
            <span style={styles.couponsTitle}>🎟️ Available Offers</span>
            <span style={styles.couponsSubtitle}>Valid for salon visits only. Tap to copy</span>
          </div>
          <div style={styles.couponsList}>
            {coupons.map((coupon) => (
              <div key={coupon.id} style={styles.couponCard} onClick={() => handleCopyCode(coupon.code)}>
                <div style={styles.couponLeft}>
                  <div style={styles.couponDiscount}>
                    {coupon.discount_type === "percentage"
                      ? `${coupon.discount_value}% OFF`
                      : `₹${coupon.discount_value} OFF`}
                  </div>
                  {coupon.min_amount > 0 && (
                    <p style={styles.couponMin}>Min. order ₹{coupon.min_amount}</p>
                  )}
                  <p style={styles.couponExpiry}>
                    Valid till {new Date(coupon.valid_till).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </p>
                </div>
                <div style={styles.couponRight}>
                  <div style={styles.couponCode}>{coupon.code}</div>
                  <div style={styles.copyBtn}>
                    {copiedCode === coupon.code ? "✅ Copied!" : "📋 Tap to Copy"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        <div style={styles.tabRow}>
          <button onClick={() => setActiveTab("services")}
            style={{ ...styles.tab, ...(activeTab === "services" ? styles.tabActive : {}) }}>
            ✂️ Services ({shop.services?.length || 0})
          </button>
          {homeEnabled && (
            <button onClick={() => setActiveTab("home")}
              style={{ ...styles.tab, ...(activeTab === "home" ? styles.tabActiveGreen : {}) }}>
              🏠 Home ({homeServices.length})
            </button>
          )}
          <button onClick={() => setActiveTab("reviews")}
            style={{ ...styles.tab, ...(activeTab === "reviews" ? styles.tabActive : {}) }}>
            ⭐ Reviews ({shop.reviews?.length || 0})
          </button>
        </div>

        {/* Salon Services Tab */}
        {activeTab === "services" && (
          <div style={styles.tabContent}>
            {!shop.services?.length ? (
              <div style={styles.emptyTab}>
                <p style={{ fontSize: "36px" }}>✂️</p>
                <p style={{ color: "var(--text-muted)" }}>No services listed yet.</p>
              </div>
            ) : (
              shop.services.map((service) => <ServiceCard key={service.id} service={service} />)
            )}
          </div>
        )}

        {/* Home Services Tab */}
        {activeTab === "home" && homeEnabled && (
          <div style={styles.tabContent}>

            {/* Info box */}
            <div style={styles.homeServiceInfo}>
              <p style={styles.homeInfoText}>
                🏠 These services are available at your doorstep. A small advance is collected online to confirm your booking. The stylist will WhatsApp you to confirm the exact time.
              </p>
            </div>

            {/* Cancellation warning */}
            <div style={styles.cancelWarningBox}>
              <div style={styles.cancelWarningTop}>
                <span style={styles.cancelWarningIcon}>🚫</span>
                <span style={styles.cancelWarningTitle}>Non-Cancellable Booking</span>
              </div>
              <p style={styles.cancelWarningText}>
                Home service bookings <strong>cannot be cancelled</strong> once the advance is paid. The advance amount is strictly non-refundable. Please confirm your availability before booking.
              </p>
            </div>

            {homeServices.filter(Boolean).map((service) => (
              <div key={service.id} style={styles.homeServiceCard}>
                <div style={styles.serviceLeft}>
                  <h3 style={styles.serviceName}>{service.name}</h3>
                  {service.description && <p style={styles.serviceDesc}>{service.description}</p>}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                    <span style={styles.serviceDuration}>⏱ {service.duration} mins</span>
                    <span style={styles.advanceBadge}>Advance ₹{service.advance_amount}</span>
                  </div>
                </div>
                <div style={styles.serviceRight}>
                  <span style={styles.servicePrice}>₹{service.price}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <div style={styles.tabContent}>
            {!shop.reviews?.length ? (
              <div style={styles.emptyTab}>
                <p style={{ fontSize: "36px" }}>⭐</p>
                <p style={{ color: "var(--text-muted)" }}>No reviews yet. Be the first!</p>
              </div>
            ) : (
              shop.reviews.map((review) => <ReviewCard key={review.id} review={review} />)
            )}
          </div>
        )}
      </div>

      {/* Sticky Bottom Bar */}
      {shop.is_subscribed && (
        <div style={styles.stickyBar}>
          <div>
            <p style={styles.stickyText}>Ready for a fresh look?</p>
            {minPrice && <p style={styles.stickySubText}>Starting from ₹{minPrice}</p>}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {homeEnabled && (
              <button onClick={handleHomeBooking} style={styles.stickyHomeBtn}>
                🏠 Book Home
              </button>
            )}
            <button onClick={handleBookNow} style={styles.stickyBtn}>
              📅 Book Now
            </button>
          </div>
        </div>
      )}

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "" })} />
    </div>
  );
}

function ServiceCard({ service }) {
  return (
    <div style={styles.serviceCard}>
      <div style={styles.serviceLeft}>
        <h3 style={styles.serviceName}>{service.name}</h3>
        {service.description && <p style={styles.serviceDesc}>{service.description}</p>}
        <span style={styles.serviceDuration}>⏱ {service.duration} mins</span>
      </div>
      <div style={styles.serviceRight}>
        <span style={styles.servicePrice}>₹{service.price}</span>
      </div>
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <div style={styles.reviewCard}>
      <div style={styles.reviewHeader}>
        <div style={styles.reviewerInfo}>
          <div style={styles.reviewerAvatar}>{review.customer_name?.charAt(0).toUpperCase()}</div>
          <span style={styles.reviewName}>{review.customer_name}</span>
        </div>
        <div style={styles.stars}>
          {[1,2,3,4,5].map((star) => (
            <span key={star} style={{ color: star <= review.rating ? "#f5a623" : "var(--border)", fontSize: "14px" }}>★</span>
          ))}
        </div>
      </div>
      {review.comment && <p style={styles.reviewComment}>{review.comment}</p>}
      <p style={styles.reviewDate}>
        {new Date(review.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
      </p>
    </div>
  );
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const [hourStr, minuteStr] = timeStr.split(":");
  let hour = parseInt(hourStr);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minuteStr} ${ampm}`;
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)", paddingBottom: "80px", overflowX: "hidden", width: "100%", maxWidth: "100vw" },
  center: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "12px" },
  loadingSpinner: { fontSize: "48px" },
  bannerWrap: { position: "relative", width: "100%", maxWidth: "100vw", height: "260px", overflow: "hidden" },
  bannerImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  bannerPlaceholder: { width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px", background: "linear-gradient(135deg, #1a1a2e, #e94560)" },
  bannerPlaceholderLetter: { color: "white", fontWeight: "900", fontSize: "48px", opacity: 0.4 },
  bannerOverlay: { position: "absolute", bottom: 0, left: 0, right: 0, height: "65%", background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, transparent 100%)" },
  backBtnOverlay: { position: "absolute", top: "14px", left: "16px", zIndex: 10, background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.2)", color: "white", padding: "8px 14px", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  backBtnSmall: { background: "none", border: "1px solid var(--border)", padding: "8px 16px", borderRadius: "6px", cursor: "pointer", fontSize: "14px", color: "var(--text-secondary)" },
  bannerNameWrap: { position: "absolute", bottom: "16px", left: "16px", right: "16px", zIndex: 10 },
  bannerName: { fontSize: "clamp(1.2rem, 4vw, 2rem)", fontWeight: "900", color: "white", margin: "0 0 4px", textShadow: "0 2px 8px rgba(0,0,0,0.5)", wordBreak: "break-word" },
  bannerCity: { color: "rgba(255,255,255,0.85)", fontSize: "13px", margin: "0 0 6px", textShadow: "0 1px 4px rgba(0,0,0,0.5)" },
  homeBadgeBanner: { display: "inline-block", background: "rgba(76,175,80,0.85)", color: "white", fontSize: "11px", padding: "3px 10px", borderRadius: "20px", fontWeight: "600" },
  infoCard: { background: "var(--bg-card)", margin: "0 auto", maxWidth: "900px", padding: "16px", border: "1px solid var(--border)", borderTop: "none", boxSizing: "border-box", width: "100%" },
  metaRow: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" },
  ratingBadge: { background: "rgba(245,166,35,0.15)", color: "#f5a623", padding: "5px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold", border: "1px solid rgba(245,166,35,0.3)" },
  hoursBadge: { background: "rgba(76,175,80,0.15)", color: "#4caf50", padding: "5px 10px", borderRadius: "20px", fontSize: "12px", border: "1px solid rgba(76,175,80,0.3)" },
  homeServiceBadge: { background: "rgba(76,175,80,0.15)", color: "#4caf50", padding: "5px 10px", borderRadius: "20px", fontSize: "12px", border: "1px solid rgba(76,175,80,0.3)", fontWeight: "600" },
  shopDesc: { color: "var(--text-muted)", marginBottom: "12px", lineHeight: 1.6, fontSize: "14px" },
  phone: { color: "var(--text-muted)", marginBottom: "12px", fontSize: "14px" },
  notSubscribed: { background: "rgba(255,152,0,0.1)", color: "#ff9800", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", marginBottom: "12px", border: "1px solid rgba(255,152,0,0.3)" },
  btnGroup: { display: "flex", flexDirection: "column", gap: "10px" },
  bookBtn: { background: "var(--accent)", color: "white", padding: "13px", border: "none", borderRadius: "10px", fontSize: "15px", cursor: "pointer", fontWeight: "bold", width: "100%", boxSizing: "border-box" },
  bookHomeBtnCard: { background: "#4caf50", color: "white", padding: "13px", border: "none", borderRadius: "10px", fontSize: "15px", cursor: "pointer", fontWeight: "bold", width: "100%", boxSizing: "border-box" },
  couponsSection: { maxWidth: "900px", margin: "0 auto", padding: "14px 16px", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)", borderLeft: "1px solid var(--border)", borderRight: "1px solid var(--border)", boxSizing: "border-box", width: "100%", overflowX: "hidden" },
  couponsHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "6px" },
  couponsTitle: { color: "white", fontWeight: "bold", fontSize: "14px" },
  couponsSubtitle: { color: "var(--text-muted)", fontSize: "12px" },
  couponsList: { display: "flex", flexDirection: "column", gap: "10px" },
  couponCard: { display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--bg-card)", borderRadius: "12px", padding: "12px 14px", border: "1px dashed rgba(233,69,96,0.4)", cursor: "pointer", boxSizing: "border-box", width: "100%", overflow: "hidden" },
  couponLeft: { flex: 1, minWidth: 0 },
  couponDiscount: { color: "var(--accent)", fontWeight: "900", fontSize: "1rem", marginBottom: "4px" },
  couponMin: { color: "var(--text-muted)", fontSize: "11px", margin: "0 0 2px" },
  couponExpiry: { color: "var(--text-muted)", fontSize: "11px", margin: 0 },
  couponRight: { textAlign: "right", flexShrink: 0, marginLeft: "10px" },
  couponCode: { background: "rgba(233,69,96,0.15)", color: "var(--accent)", padding: "5px 10px", borderRadius: "8px", fontSize: "13px", fontWeight: "bold", letterSpacing: "1px", marginBottom: "6px", border: "1px solid rgba(233,69,96,0.3)" },
  copyBtn: { color: "var(--text-muted)", fontSize: "11px" },
  tabsContainer: { maxWidth: "900px", margin: "0 auto 20px", background: "var(--bg-card)", border: "1px solid var(--border)", borderTop: "none", overflow: "hidden", width: "100%", boxSizing: "border-box" },
  tabRow: { display: "flex", borderBottom: "1px solid var(--border)" },
  tab: { flex: 1, padding: "14px 8px", border: "none", background: "none", cursor: "pointer", fontSize: "13px", color: "var(--text-muted)", fontWeight: "500" },
  tabActive: { flex: 1, padding: "14px 8px", border: "none", background: "rgba(233,69,96,0.08)", cursor: "pointer", fontSize: "13px", color: "var(--accent)", fontWeight: "bold", borderBottom: "2px solid var(--accent)" },
  tabActiveGreen: { flex: 1, padding: "14px 8px", border: "none", background: "rgba(76,175,80,0.08)", cursor: "pointer", fontSize: "13px", color: "#4caf50", fontWeight: "bold", borderBottom: "2px solid #4caf50" },
  tabContent: { padding: "14px" },
  emptyTab: { textAlign: "center", padding: "40px", color: "var(--text-muted)" },
  homeServiceInfo: { background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.2)", borderRadius: "10px", padding: "12px", marginBottom: "10px" },
  homeInfoText: { margin: 0, fontSize: "13px", color: "var(--text-secondary)", lineHeight: 1.6 },

  // Cancellation warning
  cancelWarningBox: { background: "rgba(233,69,96,0.08)", border: "1px solid rgba(233,69,96,0.3)", borderRadius: "10px", padding: "12px 14px", marginBottom: "14px" },
  cancelWarningTop: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" },
  cancelWarningIcon: { fontSize: "16px" },
  cancelWarningTitle: { color: "#ff6b6b", fontWeight: "bold", fontSize: "13px" },
  cancelWarningText: { color: "rgba(255,107,107,0.85)", fontSize: "12px", lineHeight: 1.6, margin: 0 },

  homeServiceCard: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", border: "1px solid rgba(76,175,80,0.25)", borderRadius: "10px", marginBottom: "10px", background: "rgba(76,175,80,0.04)", boxSizing: "border-box" },
  advanceBadge: { background: "rgba(76,175,80,0.15)", color: "#4caf50", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", border: "1px solid rgba(76,175,80,0.3)", display: "inline-block" },
  serviceCard: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", border: "1px solid var(--border)", borderRadius: "10px", marginBottom: "10px", background: "var(--bg-secondary)", boxSizing: "border-box" },
  serviceLeft: { flex: 1, minWidth: 0 },
  serviceName: { fontSize: "14px", fontWeight: "bold", marginBottom: "4px", color: "white", wordBreak: "break-word" },
  serviceDesc: { color: "var(--text-muted)", fontSize: "12px", marginBottom: "6px" },
  serviceDuration: { background: "rgba(79,195,247,0.15)", color: "#4fc3f7", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", border: "1px solid rgba(79,195,247,0.3)", display: "inline-block" },
  serviceRight: { marginLeft: "12px", flexShrink: 0 },
  servicePrice: { fontSize: "1.2rem", fontWeight: "bold", color: "var(--accent)" },
  reviewCard: { padding: "14px", border: "1px solid var(--border)", borderRadius: "10px", marginBottom: "10px", background: "var(--bg-secondary)", boxSizing: "border-box" },
  reviewHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", flexWrap: "wrap", gap: "6px" },
  reviewerInfo: { display: "flex", alignItems: "center", gap: "10px" },
  reviewerAvatar: { width: "32px", height: "32px", borderRadius: "50%", background: "var(--accent)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "14px", flexShrink: 0 },
  reviewName: { fontWeight: "bold", color: "white", fontSize: "14px" },
  stars: { display: "flex" },
  reviewComment: { color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: "8px", fontSize: "14px", wordBreak: "break-word" },
  reviewDate: { color: "var(--text-muted)", fontSize: "11px" },
  stickyBar: { position: "fixed", bottom: 0, left: 0, right: 0, background: "var(--bg-secondary)", borderTop: "1px solid var(--border)", padding: "12px 16px", paddingBottom: "max(12px, env(safe-area-inset-bottom))", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 -4px 20px rgba(0,0,0,0.3)", zIndex: 100, width: "100%", boxSizing: "border-box" },
  stickyText: { margin: 0, fontWeight: "bold", fontSize: "14px", color: "white" },
  stickySubText: { margin: 0, color: "var(--text-muted)", fontSize: "12px" },
  stickyHomeBtn: { background: "#4caf50", color: "white", padding: "11px 16px", border: "none", borderRadius: "8px", fontSize: "14px", cursor: "pointer", fontWeight: "bold", whiteSpace: "nowrap", flexShrink: 0 },
  stickyBtn: { background: "var(--accent)", color: "white", padding: "11px 20px", border: "none", borderRadius: "8px", fontSize: "14px", cursor: "pointer", fontWeight: "bold", whiteSpace: "nowrap", flexShrink: 0 },
};